import React, { useState } from 'react';

import { Box, Button, MenuItem, Popover, Stack, TextField, Typography } from '@mui/material';
import { DateRangeCalendar, type DateRange } from '@mui/x-date-pickers-pro';
import type { Dayjs } from 'dayjs';

import { presetLabel } from './format';
import type { LensDocument } from './types';
import type { BoardEditor } from './useBoardEdit';

const STANDARD = ['1d', '7d', '28d', '90d', 'mtd', 'last_month', 'qtd', 'last_quarter', 'ytd', 'last_year', 'fytd', 'all'];
const UNITS = [
  { unit: 'd', label: 'days' },
  { unit: 'w', label: 'weeks' },
  { unit: 'm', label: 'months' },
];

const COMPARED: Array<[string, string]> = [
  ['prior', 'Prior period'],
  ['ly', 'Same period last year'],
  ['none', 'None'],
];

type CustomPeriodProps = {
  editor?: BoardEditor;
  document?: LensDocument;
  presets: Array<string>;
  period: string | undefined;
  disabled: boolean;
  onPick: (period: string) => void;
};

export default function CustomPeriod({ presets, period, disabled, onPick, editor, document }: CustomPeriodProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [count, setCount] = useState('14');
  const [unit, setUnit] = useState('d');
  const [range, setRange] = useState<DateRange<Dayjs>>([null, null]);
  const custom = Boolean(period && !presets.includes(period));
  const pick = (next: string) => {
    onPick(next);
    setAnchor(null);
  };
  const [from, to] = range;
  const steps = Number(count);
  const compare = document?.period.compare.filter((entry) => typeof entry === 'string').join(',') || 'none';
  const compared = COMPARED.some(([value]) => value === compare) ? COMPARED : [...COMPARED, [compare, 'Prior period and last year'] as [string, string]];

  return (
    <>
      <button className={custom ? 'on' : ''} disabled={disabled} onClick={(event) => setAnchor(event.currentTarget)} type="button">
        {custom && period ? presetLabel(period) : 'Custom…'}
      </button>
      <Popover
        anchorEl={anchor}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() => setAnchor(null)}
        open={Boolean(anchor)}
      >
        <Stack direction="row" divider={<Box sx={{ borderLeft: 1, borderColor: 'divider' }} />} sx={{ p: 1.5, gap: 1.5 }}>
          <Stack sx={{ minWidth: 140 }}>
            {STANDARD.filter((preset) => !presets.includes(preset)).map((preset) => (
              <Button key={preset} onClick={() => pick(preset)} size="small" sx={{ justifyContent: 'flex-start' }}>
                {presetLabel(preset)}
              </Button>
            ))}
          </Stack>
          <Stack sx={{ gap: 1.5 }}>
            <Stack alignItems="center" direction="row" sx={{ gap: 1 }}>
              <Typography variant="body2">Last</Typography>
              <TextField inputProps={{ min: 1, 'aria-label': 'How many' }} onChange={(event) => setCount(event.target.value)} size="small" sx={{ width: 72 }} type="number" value={count} />
              <TextField onChange={(event) => setUnit(event.target.value)} select size="small" value={unit}>
                {UNITS.map((option) => (
                  <MenuItem key={option.unit} value={option.unit}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button disabled={!Number.isInteger(steps) || steps < 1} onClick={() => pick(`${steps}${unit}`)} size="small" variant="outlined">
                Apply
              </Button>
            </Stack>
            <DateRangeCalendar calendars={2} disableFuture onChange={setRange} value={range} />
            <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ gap: 1 }}>
              {editor ? (
                <Stack alignItems="center" direction="row" sx={{ gap: 1 }}>
                  <Typography id="compared-with" variant="body2">
                    Compared with
                  </Typography>
                  <TextField
                    disabled={editor.pending}
                    onChange={(event) => editor.apply({ op: 'set', id: 'lens', patch: { period: { compare: event.target.value === 'none' ? [] : event.target.value.split(',') } } })}
                    select
                    size="small"
                    slotProps={{ select: { SelectDisplayProps: { 'aria-labelledby': 'compared-with' } as React.HTMLAttributes<HTMLDivElement> } }}
                    value={compare}
                  >
                    {compared.map(([value, name]) => (
                      <MenuItem key={value} value={value}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              ) : null}
              <Stack alignItems="center" direction="row" sx={{ gap: 1 }}>
                <Typography color="text.secondary" variant="body2">
                  {from ? `${from.format('D MMM YYYY')} – ${to ? to.format('D MMM YYYY') : '…'}` : 'Pick a start and an end day'}
                </Typography>
                <Button
                  disabled={!from || !to}
                  onClick={() => from && to && pick(`${from.format('YYYY-MM-DD')}..${to.format('YYYY-MM-DD')}`)}
                  size="small"
                  variant="contained"
                >
                  Apply
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
